import { Controller } from '@hotwired/stimulus'

// Checkout de uma página: bumps somando no cupom do pedido, abas Pix/Cartão,
// rótulo do botão por meio de pagamento, validação no blur e no envio
// (mesmas mensagens do protótipo), captura de lead e sugestão de domínio.
const DOMAINS = ['gmail.com', 'hotmail.com', 'outlook.com', 'yahoo.com.br', 'yahoo.com', 'icloud.com', 'live.com', 'uol.com.br', 'bol.com.br', 'terra.com.br']

const digits = (v) => (v || '').replace(/\D/g, '')

function cpfOk(value) {
  const d = digits(value)
  if (d.length !== 11 || /^(\d)\1+$/.test(d)) return false
  for (let t = 9; t < 11; t++) {
    let s = 0
    for (let i = 0; i < t; i++) s += Number(d[i]) * (t + 1 - i)
    if (((s * 10) % 11) % 10 !== Number(d[t])) return false
  }
  return true
}

const RULES = {
  email: (v) => {
    if (!v) return 'Digite seu e-mail. É para lá que vai o acesso.'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim())) return 'Este e-mail parece incompleto. Confira o @ e o final.'
    return ''
  },
  name: (v) => (v.trim().split(/\s+/).filter(Boolean).length < 2 ? 'Digite nome e sobrenome.' : ''),
  whatsapp: (v) => (digits(v).length < 10 ? 'Celular incompleto. Confira o DDD e o número.' : ''),
  cpf: (v) => {
    if (digits(v).length < 11) return 'CPF incompleto. Confira os 11 números.'
    return cpfOk(v) ? '' : 'Este CPF não confere. Veja se algum número trocou de lugar.'
  }
}

const ICON_ERRO = '<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.75"><circle cx="8" cy="8" r="6.8"/><path d="M8 4.5v4.2M8 10.8v.6"/></svg>'
const SPIN = '<span class="spin" aria-hidden="true"></span>'

export default class extends Controller {
  static targets = ['bump', 'linha', 'total', 'method', 'cardSection', 'pixSection', 'recusado', 'submit', 'installments', 'email', 'emailSuggestion', 'name', 'whatsapp']
  static values = { baseCents: Number, leadUrl: String }

  connect() { this.refresh() }

  get totalCents() {
    return this.bumpTargets.filter((b) => b.checked).reduce((sum, b) => sum + Number(b.dataset.priceCents), this.baseCentsValue)
  }

  get method() {
    return this.methodTargets.find((m) => m.checked)?.value || 'pix'
  }

  refresh() {
    const total = this.totalCents
    const card = this.method === 'card'

    // cupom: mostra as linhas dos bumps marcados e renumera
    const marcados = new Set(this.bumpTargets.filter((b) => b.checked).map((b) => b.value))
    let n = 2
    this.linhaTargets.forEach((linha) => {
      const on = marcados.has(linha.dataset.stepId)
      linha.hidden = !on
      if (on) linha.querySelector('[data-num]').textContent = String(n++).padStart(2, '0')
    })
    this.bumpTargets.forEach((b) => b.closest('.bump')?.classList.toggle('on', b.checked))
    this.totalTarget.textContent = this.cupom(total)

    this.cardSectionTarget.hidden = !card
    if (this.hasPixSectionTarget) this.pixSectionTarget.hidden = card
    if (!card && this.hasRecusadoTarget) this.recusadoTarget.hidden = true

    this.submitTarget.textContent = card ? `Pagar ${this.money(total)}` : `Gerar Pix de ${this.money(total)}`
    this.submitTarget.dataset.turboSubmitsWith = SPIN + (card ? 'Processando pagamento…' : 'Gerando seu Pix…')
    this.updateInstallments(total)
    this.dispatch('total', { detail: { totalCents: total, method: this.method } })
  }

  updateInstallments(total) {
    if (!this.hasInstallmentsTarget || this.installmentsTarget.dataset.mpManaged) return
    Array.from(this.installmentsTarget.options).forEach((option) => {
      const n = Number(option.value)
      option.textContent = n === 1 ? `1x de ${this.money(total)} (à vista)` : `${n}x de ${this.money(Math.ceil(total / n))}`
    })
  }

  usePix() {
    const pix = this.methodTargets.find((m) => m.value === 'pix')
    if (!pix) return
    pix.checked = true
    this.refresh()
    pix.focus()
  }

  // ---------- validação ----------
  fieldFor(name) { return this.element.querySelector(`[data-campo="${name}"]`) }

  showError(name, message) {
    const input = this.fieldFor(name)
    const el = this.element.querySelector(`#${name}-erro`)
    if (!input || !el) return !message
    if (message) {
      el.innerHTML = `${ICON_ERRO}<span></span>`
      el.querySelector('span').textContent = message
      el.hidden = false
      input.setAttribute('aria-invalid', 'true')
    } else {
      el.hidden = true
      input.removeAttribute('aria-invalid')
    }
    return !message
  }

  validateField(event) {
    const input = event.currentTarget
    const name = input.dataset.campo
    if (input.value) this.showError(name, RULES[name](input.value))
  }

  clearField(event) {
    const input = event.currentTarget
    const name = input.dataset.campo
    if (input.getAttribute('aria-invalid') && !RULES[name](input.value)) this.showError(name, '')
  }

  validateAll() {
    let first = null
    Object.keys(RULES).forEach((name) => {
      const input = this.fieldFor(name)
      if (input && !this.showError(name, RULES[name](input.value)) && !first) first = input
    })
    if (first) first.focus()
    return !first
  }

  submit(event) {
    if (this.validateAll()) return
    event.preventDefault()
    event.stopImmediatePropagation()
  }

  // ---------- lead e e-mail ----------
  captureLead() {
    if (!this.leadUrlValue) return
    const body = new FormData()
    body.append('email', this.emailTarget.value)
    body.append('name', this.nameTarget.value)
    body.append('whatsapp', this.whatsappTarget.value)
    fetch(this.leadUrlValue, {
      method: 'POST',
      body,
      headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content },
      credentials: 'same-origin'
    }).catch(() => {})
  }

  suggestEmail() {
    const value = this.emailTarget.value.trim()
    const at = value.lastIndexOf('@')
    const hint = this.emailSuggestionTarget
    hint.hidden = true
    if (at < 1) return
    const domain = value.slice(at + 1).toLowerCase()
    if (!domain || DOMAINS.includes(domain)) return
    const best = DOMAINS.map((d) => [d, this.distance(domain, d)]).sort((a, b) => a[1] - b[1])[0]
    if (!best || best[1] > 2) return

    hint.innerHTML = 'Você quis dizer <strong></strong>? <button type="button">Corrigir</button>'
    hint.querySelector('strong').textContent = `@${best[0]}`
    hint.querySelector('button').addEventListener('click', () => {
      this.emailTarget.value = value.slice(0, at + 1) + best[0]
      hint.hidden = true
      this.showError('email', '')
      this.emailTarget.focus()
    })
    hint.hidden = false
  }

  distance(a, b) {
    const dp = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)])
    for (let j = 0; j <= b.length; j++) dp[0][j] = j
    for (let i = 1; i <= a.length; i++)
      for (let j = 1; j <= b.length; j++)
        dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1))
    return dp[a.length][b.length]
  }

  money(cents) {
    return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
  }

  cupom(cents) {
    return (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }
}
